import { Injectable } from '@angular/core';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces de detalle — los nombres y valores de los campos coinciden
// exactamente con los payloads que esperan guardarTarifas / guardarCampanias /
// guardarSubcuentas, por lo que el compañero puede usarlos directamente.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Un item del array `tarifas` que espera guardarTarifas (tarifa principal).
 * fueSeleccionada y esAnexo son siempre true/false para la tarifa del programa.
 */
export interface ProgramaTarifaDetalle {
  /** TARIFA_CODIGO — código limpio, sin texto decorativo. */
  tari: string;
  nombreTarifa: string | null;
  valorMensualBase: number | null;
  /** Valor principal para guardar en BD. */
  valorMensualIva: number | null;
  porcentajeIva: number | null;
  esTarifaAsociado: boolean | null;
  /** Siempre true para la tarifa seleccionada. */
  fueSeleccionada: true;
  /** Siempre false — este item es la tarifa del programa, no del anexo. */
  esAnexo: false;
  /** RQ004.2: Tipo de tarifa - 'I' = cantidad integrantes, 'C' = comportamiento actual. */
  tariTipo?: 'I' | 'C' | null;
}

/**
 * Un item del array `tarifas` que espera guardarTarifas (tarifa del anexo).
 * fueSeleccionada y esAnexo son siempre true para el anexo.
 * El campo `clave` es solo para la precarga del modal (UI) y se ignora en BD.
 */
export interface ProgramaAnexoDetalle {
  /** Clave de selección en UI — no se envía a BD. */
  clave: string;
  tari: string;
  /** Nombre del anexo → guardarTarifas.nombreTarifa */
  nombreTarifa: string;
  valorMensualIva: number | null;
  valorMensualBase: null;
  porcentajeIva: null;
  esTarifaAsociado: null;
  /** Siempre true para el item de anexo. */
  fueSeleccionada: true;
  /** Siempre true — identifica que este item es el anexo. */
  esAnexo: true;
}

/**
 * Un item del array `campanias` que espera guardarCampanias.
 * Los nombres de campo coinciden exactamente con los que construye
 * buildCampaniasPayloadFromSeleccion en NetworkingBeneficiary.
 * Las fechas (inicioVigencia/finVigencia) se almacenan tal cual vienen
 * del servicio; el compañero debe aplicar normalizeCampaignDate al guardar.
 */
export interface ProgramaCampaniaDetalle {
  /** codigo_campania del servicio → codCampComercial en BD. */
  codCampComercial: string;
  /** nombre del servicio → nombreCampania en BD. */
  nombreCampania: string;
  /** Siempre true para la campaña seleccionada. */
  fueSeleccionada: true;
  /** id interno de la campaña (referencia, no se envía a BD). */
  idCampania?: number;
  tipoCampania?: string;
  modalidad?: string;
  cuenta?: number | string;
  subcuenta?: string;
  tipoPlan?: string;
  parentesco?: string;
  edadDesde?: number;
  edadHasta?: number;
  cantBenefDesde?: number;
  cantBenefHasta?: number;
  acumulable?: boolean;
  pagoDebitoAuto?: boolean;
  aplicaProntoPago?: boolean;
  observaciones?: string;
  usuarioActualizacion?: string;
  lineaNegocio?: string;
  grupoPrograma?: string;
  programa?: string;
  /** Fecha en formato raw del servicio; normalizar con normalizeCampaignDate al guardar. */
  inicioVigencia?: string;
  /** Fecha en formato raw del servicio; normalizar con normalizeCampaignDate al guardar. */
  finVigencia?: string;
  estado?: boolean;
}

/** Un programa seleccionado con todos sus datos de servicio listos para BD. */
export interface ProgramaItemStore {
  // ── Identidad ────────────────────────────────────────────────────────────
  /** "MI" | "SAO" | "CEM" (en formato persistido — SAO, no SO) */
  lineaNegocio: string;
  /** Código del programa, ej. "OPLF" */
  programa: string;
  nombrePrograma: string;
  grupo: string;
  grupoPrograma?: string;

  // ── Subcuenta ────────────────────────────────────────────────────────────
  /** Nombre para mostrar en UI (deno) */
  subcuenta: string;
  /** Código interno para guardarSubcuentas.subcta */
  subcta: string;

  // ── Tarifa principal ─────────────────────────────────────────────────────
  /** Código seleccionado (tari limpio) */
  tarifa: string;
  /** Detalle completo para armar guardarTarifas */
  tarifaDetalle: ProgramaTarifaDetalle | null;
  /** RQ004.2: Tipo de tarifa - 'I' = cantidad integrantes, 'C' = comportamiento actual. */
  tariTipo?: 'I' | 'C' | null;

  // ── Anexo ────────────────────────────────────────────────────────────────
  anexoCodigo: string;
  /** Detalle del anexo para armar el segundo item de guardarTarifas */
  anexoDetalle: ProgramaAnexoDetalle | null;
  requiereAnexo: 'si' | 'no' | null;

  // ── Campaña ──────────────────────────────────────────────────────────────
  /** codigo_campania seleccionado */
  campana: string;
  /** Detalle completo para armar guardarCampanias */
  campaniaDetalle: ProgramaCampaniaDetalle | null;

  // ── CEM ──────────────────────────────────────────────────────────────────
  coberturaValida?: boolean;

  // ── Referencias de persistencia en BD (las llena Beneficiario) ────────────
  // El contratante NO escribe estos campos (quedan undefined/null). Beneficiario
  // los setea tras guardar en BD y los usa para validaciones futuras:
  // idBenPrograma > 0 (o guardadoEnBd === true) ⇒ el programa ya fue persistido.
  /** ID de la entidad ben_programa una vez guardada en BD. Bandera de "ya guardado". */
  idBenPrograma?: number | null;
  /** ID de la tarifa principal guardada (idCotizacionTarifa). */
  idCotizacionTarifa?: number | null;
  /** ID de la tarifa de anexo guardada (idCotizacionTarifaAnexo). */
  idCotizacionTarifaAnexo?: number | null;
  /** ID de la campaña guardada. */
  idBenCampania?: number | null;
  /** Flag explícito de conveniencia para validación rápida "ya guardado". */
  guardadoEnBd?: boolean;

  /**
   * Beneficiario con switch "usar programas del contratante":
   * true si el usuario cambió este programa respecto al del titular.
   * El sync inteligente conserva estos ítems en lugar de pisarlos.
   */
  personalizado?: boolean;
  /**
   * Código del programa del contratante que este ítem reemplaza
   * (ej. cambió Dental Colectivo → Dental Especial).
   */
  codigoOrigenContratante?: string | null;
}

/**
 * Referencias de persistencia en BD que Beneficiario asigna a un programa
 * tras guardarlo. Todos opcionales: se aplican solo los provistos.
 */
export interface ProgramaPersistenciaRef {
  idBenPrograma?: number | null;
  idCotizacionTarifa?: number | null;
  idCotizacionTarifaAnexo?: number | null;
  idBenCampania?: number | null;
  guardadoEnBd?: boolean;
}

/**
 * Envelope completo guardado en localStorage bajo la clave `programas_contratante`.
 * El campo `idCotizacion` permite al modal detectar si los datos pertenecen a
 * la cotización actual o a una diferente (en cuyo caso no se precargan).
 */
export interface ProgramasContratanteStore {
  idCotizacion: number;
  tipoDocumento: string;
  numeroDocumento: string;
  savedAt: string;
  programas: ProgramaItemStore[];
  /** RQ004.2: Datos del contratante para recálculo de tarifas tipo I. */
  datosRecalculo?: {
    edad: string;
    sexo: string;
    parentesco: string;
    cuenta?: string;
    tipoPlan?: string;
  };
}

// ─────────────────────────────────────────────────────────────────────────────

const KEY = 'programas_contratante';

@Injectable({ providedIn: 'root' })
export class ProgramaSeleccionStore {

  /** Persiste el estado completo en localStorage. */
  guardar(data: ProgramasContratanteStore): void {
    try {
      localStorage.setItem(KEY, JSON.stringify(data));
    } catch {
      // Si localStorage falla no bloqueamos el flujo
    }
  }

  /**
   * Carga el envelope desde localStorage.
   * Si el contenido es un array plano (formato legacy / flecha antigua),
   * lo adapta a envelope para no perder la precarga del modal.
   */
  cargar(): ProgramasContratanteStore | null {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed) return null;

      // Formato legacy: array plano → envelope mínimo (precarga por documento en el modal).
      if (Array.isArray(parsed)) {
        if (!parsed.length) return null;
        return {
          idCotizacion: 0,
          tipoDocumento: '',
          numeroDocumento: '',
          savedAt: '',
          programas: parsed as ProgramaItemStore[],
        };
      }

      if (typeof parsed !== 'object') return null;
      return parsed as ProgramasContratanteStore;
    } catch {
      return null;
    }
  }

  /**
   * Carga los programas solo si el `idCotizacion` y el `numeroDocumento`
   * coinciden con los provistos. Devuelve null si no hay match (nueva cotización).
   */
  cargarParaCotizacion(idCotizacion: number, numeroDocumento: string): ProgramaItemStore[] | null {
    const stored = this.cargar();
    if (!stored) return null;

    const idGuardado = Number(stored.idCotizacion);
    const docGuardado = (stored.numeroDocumento ?? '').trim();
    const docActual   = (numeroDocumento ?? '').trim();

    // idCotizacion = 0 (legacy/array plano o guardado antes de crear cotización):
    // permitir precarga si el documento coincide o no hay documento guardado.
    const idCoincide = !(idGuardado > 0) || idGuardado === Number(idCotizacion);
    const docCoincide = !docGuardado || !docActual || docGuardado === docActual;

    if (!idCoincide || !docCoincide) return null;

    return Array.isArray(stored.programas) && stored.programas.length > 0
      ? stored.programas
      : null;
  }

  /**
   * Marca un programa como guardado en BD, asignándole las referencias de
   * persistencia (idBenPrograma, ids de tarifa/campaña). Pensado para que
   * Beneficiario actualice un item puntual sin reconstruir todo el envelope.
   *
   * Busca el programa por su código (`programa`, ej. "OPLF"). Si no encuentra
   * el envelope o el programa, no hace nada. Si `guardadoEnBd` no se envía,
   * se infiere en true cuando llega un idBenPrograma > 0.
   *
   * @returns true si actualizó y persistió; false si no hubo match.
   */
  marcarGuardado(programa: string, refs: ProgramaPersistenciaRef): boolean {
    const stored = this.cargar();
    if (!stored || !Array.isArray(stored.programas)) return false;

    const codigo = (programa ?? '').trim();
    if (!codigo) return false;

    const item = stored.programas.find(p => (p.programa ?? '').trim() === codigo);
    if (!item) return false;

    if (refs.idBenPrograma !== undefined) item.idBenPrograma = refs.idBenPrograma;
    if (refs.idCotizacionTarifa !== undefined) item.idCotizacionTarifa = refs.idCotizacionTarifa;
    if (refs.idCotizacionTarifaAnexo !== undefined) item.idCotizacionTarifaAnexo = refs.idCotizacionTarifaAnexo;
    if (refs.idBenCampania !== undefined) item.idBenCampania = refs.idBenCampania;

    item.guardadoEnBd = refs.guardadoEnBd !== undefined
      ? refs.guardadoEnBd
      : Number(item.idBenPrograma) > 0;

    this.guardar(stored);
    return true;
  }

  /** Elimina la selección guardada. */
  limpiar(): void {
    try {
      localStorage.removeItem(KEY);
    } catch { /* no-op */ }
  }
}
